import type { IntlShape } from "@formatjs/intl";
import { Card, Flex, Progress, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { formatBookingDate } from "../../../common/utils/formatBookingRange";
import type { ProjectProgress } from "../../generated-axios-client";
import { WO_STATUS_COLORS } from "../dashboard.constants";
import styles from "../Dashboard.module.css";

const ENDING_THIS_WEEK_DAYS = 7;
const ENDING_SOON_DAYS = 30;

type WorkOrderListFilter = {
  project: string;
  attention?: string;
  status?: string;
  sort?: string;
};

type Props = {
  intl: IntlShape;
  rows: ProjectProgress[] | undefined;
  loading: boolean;
  onOpenProject: (projectId: string) => void;
  onOpenWorkOrders: (filter: WorkOrderListFilter) => void;
};

const daysUntilEnd = (row: ProjectProgress) =>
  row.end ? dayjs(row.end).diff(dayjs().startOf("day"), "day") : null;

const endTagColor = (daysLeft: number) => {
  if (daysLeft <= ENDING_THIS_WEEK_DAYS) return "red";
  if (daysLeft <= ENDING_SOON_DAYS) return "orange";
  return undefined;
};

const CountLink = ({ count, onOpen }: { count: number; onOpen: () => void }) =>
  count > 0 ? (
    <Typography.Link onClick={onOpen}>{count}</Typography.Link>
  ) : (
    <Typography.Text type="secondary">0</Typography.Text>
  );

export const ProjectProgressTable = ({
  intl,
  rows,
  loading,
  onOpenProject,
  onOpenWorkOrders,
}: Props) => {
  const columns: ColumnsType<ProjectProgress> = [
    {
      key: "project",
      title: intl.formatMessage({ id: "dashboard_projects_column_project" }),
      render: (_, row) => (
        <Flex vertical>
          <Flex gap="small" align="center">
            <Typography.Link onClick={() => onOpenProject(row.project_id)}>
              {row.project_name ?? "—"}
            </Typography.Link>
            {row.project_status === "DELAYED" && (
              <Tag color="volcano">
                {intl.formatMessage({ id: "dashboard_projects_delayed" })}
              </Tag>
            )}
          </Flex>
          {row.client_name && (
            <Typography.Text type="secondary">{row.client_name}</Typography.Text>
          )}
        </Flex>
      ),
    },
    {
      key: "progress",
      title: intl.formatMessage({ id: "dashboard_projects_column_progress" }),
      sorter: (first, second) =>
        first.done_work_orders / (first.total_work_orders || 1) -
        second.done_work_orders / (second.total_work_orders || 1),
      render: (_, row) => {
        const total = row.total_work_orders || 1;
        return (
          <Tooltip
            title={intl.formatMessage(
              { id: "dashboard_projects_progress_breakdown" },
              {
                done: row.done_work_orders,
                review: row.in_review_work_orders,
                open: row.open_work_orders,
              }
            )}
          >
            <Flex vertical className={styles.projectProgress}>
              <Progress
                size="small"
                showInfo={false}
                percent={
                  ((row.done_work_orders + row.in_review_work_orders) / total) * 100
                }
                strokeColor={WO_STATUS_COLORS.IN_REVIEW}
                success={{
                  percent: (row.done_work_orders / total) * 100,
                  strokeColor: WO_STATUS_COLORS.DONE,
                }}
              />
              <Typography.Text type="secondary">
                {intl.formatMessage(
                  { id: "dashboard_projects_done_of_total" },
                  { done: row.done_work_orders, total: row.total_work_orders }
                )}
              </Typography.Text>
            </Flex>
          </Tooltip>
        );
      },
    },
    {
      key: "open",
      title: intl.formatMessage({ id: "dashboard_projects_column_open" }),
      sorter: (first, second) => first.open_work_orders - second.open_work_orders,
      render: (_, row) => (
        <CountLink
          count={row.open_work_orders}
          onOpen={() => onOpenWorkOrders({ project: row.project_id })}
        />
      ),
    },
    {
      key: "overdue",
      title: intl.formatMessage({ id: "dashboard_projects_column_overdue" }),
      sorter: (first, second) => first.overdue_work_orders - second.overdue_work_orders,
      render: (_, row) => (
        <CountLink
          count={row.overdue_work_orders}
          onOpen={() =>
            onOpenWorkOrders({
              project: row.project_id,
              attention: "overdue",
              sort: "deadline",
            })
          }
        />
      ),
    },
    {
      key: "delayed",
      title: intl.formatMessage({ id: "dashboard_projects_column_delayed" }),
      sorter: (first, second) => first.delayed_work_orders - second.delayed_work_orders,
      render: (_, row) => (
        <CountLink
          count={row.delayed_work_orders}
          onOpen={() => onOpenWorkOrders({ project: row.project_id, status: "DELAYED" })}
        />
      ),
    },
    {
      key: "end",
      title: intl.formatMessage({ id: "dashboard_projects_column_ends" }),
      sorter: (first, second) =>
        (daysUntilEnd(first) ?? Number.MAX_SAFE_INTEGER) -
        (daysUntilEnd(second) ?? Number.MAX_SAFE_INTEGER),
      render: (_, row) => {
        const daysLeft = daysUntilEnd(row);
        if (!row.end || daysLeft === null) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        const color = endTagColor(daysLeft);
        return color ? (
          <Tag color={color}>{formatBookingDate(intl, row.end)}</Tag>
        ) : (
          formatBookingDate(intl, row.end)
        );
      },
    },
  ];

  return (
    <Card
      className={styles.fullHeight}
      title={intl.formatMessage({ id: "dashboard_projects_title" })}
    >
      <Table<ProjectProgress>
        rowKey="project_id"
        size="small"
        columns={columns}
        dataSource={rows ?? []}
        loading={loading}
        pagination={{ pageSize: 6, hideOnSinglePage: true, size: "small" }}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: intl.formatMessage({ id: "dashboard_projects_empty" }) }}
      />
    </Card>
  );
};
